import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Alert
} from '@mui/material';
import {
  Add,
  Send,
  Message,
  Sms,
  WhatsApp,
  Email,
  Group,
  Person,
  Check,
  Error,
  Schedule,
  Search,
  Refresh
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Message as MessageType, Patient } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const messageTemplates = [
  {
    id: 'medication_reminder',
    title: 'Medication Reminder',
    content: 'Dear {patient_name}, this is a reminder to take your {medication} at {time}. Please contact us if you have any questions.',
    category: 'reminder'
  },
  {
    id: 'appointment_reminder',
    title: 'Appointment Reminder',
    content: 'Hello {patient_name}, you have an appointment scheduled for {date} at {time}. Please arrive 15 minutes early.',
    category: 'appointment'
  },
  {
    id: 'test_results',
    title: 'Test Results Available',
    content: 'Dear {patient_name}, your test results are ready. Please log in to your portal or call us to discuss the results.',
    category: 'results'
  },
  {
    id: 'emergency_alert',
    title: 'Emergency Alert',
    content: 'URGENT: {patient_name}, we have detected concerning health readings. Please contact us immediately or visit the nearest emergency room.',
    category: 'emergency'
  }
];

export default function MessagesPage() {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    recipients: [] as string[],
    subject: '',
    content: '',
    type: 'sms' as 'sms' | 'whatsapp' | 'email',
    scheduleDate: '',
    scheduleTime: ''
  });

  useEffect(() => {
    // Mock patients data
    const mockPatients: Patient[] = [
      {
        id: '1',
        patient_id: 'P001',
        name: 'John Smith',
        age: 65,
        gender: 'male',
        phone: '+1234567890',
        email: 'john@email.com',
        medicalHistory: 'Hypertension, Diabetes',
        allergies: '',
        doctorId: user?.id || 'doc1',
        doctorName: 'Dr. Smith',
        emergencyContactName: 'Jane Smith',
        emergencyContactPhone: '+1234567891',
        emergencyContactRelationship: 'Wife',
        is_active: true,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z',
        last_visit_date: null,
        healthStatus: 'critical'
      },
      {
        id: '2',
        patient_id: 'P002',
        name: 'Mary Johnson',
        age: 58,
        gender: 'female',
        phone: '+1234567892',
        email: 'mary@email.com',
        medicalHistory: 'Heart condition',
        allergies: '',
        doctorId: user?.id || 'doc1',
        doctorName: 'Dr. Smith',
        emergencyContactName: 'Tom Johnson',
        emergencyContactPhone: '+1234567893',
        emergencyContactRelationship: 'Husband',
        is_active: true,
        created_at: '2024-01-02T00:00:00Z',
        updated_at: '2024-01-02T00:00:00Z',
        last_visit_date: null,
        healthStatus: 'warning'
      }
    ];

    // Mock messages data
    const mockMessages: MessageType[] = [
      {
        id: '1',
        senderId: user?.id || 'doc1',
        senderName: user?.full_name || user?.username || 'Dr. Smith',
        recipientId: '1',
        recipientName: 'John Smith',
        subject: 'Medication Reminder',
        content: 'Dear John, this is a reminder to take your blood pressure medication at 8:00 AM.',
        type: 'sms',
        status: 'sent',
        timestamp: '2024-01-07T08:00:00Z'
      },
      {
        id: '2',
        senderId: user?.id || 'doc1',
        senderName: user?.full_name || user?.username || 'Dr. Smith',
        recipientId: '2',
        recipientName: 'Mary Johnson',
        subject: 'Appointment Reminder',
        content: 'Hello Mary, you have an appointment scheduled for tomorrow at 10:30 AM.',
        type: 'whatsapp',
        status: 'delivered',
        timestamp: '2024-01-07T15:30:00Z'
      },
      {
        id: '3',
        senderId: user?.id || 'doc1',
        senderName: user?.full_name || user?.username || 'Dr. Smith',
        recipientId: '1',
        recipientName: 'John Smith',
        subject: 'Emergency Alert',
        content: 'URGENT: We have detected high blood pressure readings. Please contact us immediately.',
        type: 'sms',
        status: 'failed',
        timestamp: '2024-01-07T12:15:00Z'
      }
    ];

    setPatients(mockPatients);
    setMessages(mockMessages);
  }, [user]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleNewMessage = () => {
    setFormData({
      recipients: [],
      subject: '',
      content: '',
      type: 'sms',
      scheduleDate: '',
      scheduleTime: ''
    });
    setSelectedTemplate('');
    setOpenDialog(true);
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = messageTemplates.find(t => t.id === templateId);
    if (template) {
      setFormData({
        ...formData,
        subject: template.title,
        content: template.content
      });
      setSelectedTemplate(templateId);
    }
  };

  const handleSendMessage = () => {
    const newMessage: MessageType = {
      id: Date.now().toString(),
      senderId: user?.id || '',
      senderName: user?.full_name || user?.username || '',
      recipientId: formData.recipients[0] || '',
      recipientName: patients.find(p => p.id === formData.recipients[0])?.name || '',
      subject: formData.subject,
      content: formData.content,
      type: formData.type,
      status: 'sent',
      timestamp: new Date().toISOString()
    };

    setMessages([newMessage, ...messages]);
    setOpenDialog(false);
  };

  const handleBulkMessage = () => {
    const bulkMessages = formData.recipients.map(recipientId => {
      const patient = patients.find(p => p.id === recipientId);
      return {
        id: `${Date.now()}-${recipientId}`,
        senderId: user?.id || '',
        senderName: user?.full_name || user?.username || '',
        recipientId,
        recipientName: patient?.name || '',
        subject: formData.subject,
        content: formData.content.replace('{patient_name}', patient?.name || ''),
        type: formData.type,
        status: 'sent' as const,
        timestamp: new Date().toISOString()
      };
    });

    setMessages([...bulkMessages, ...messages]);
    setOpenDialog(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'info';
      case 'delivered': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'sms': return <Sms />;
      case 'whatsapp': return <WhatsApp />;
      case 'email': return <Email />;
      default: return <Message />;
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'recipientName',
      headerName: 'Recipient',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32 }}>
            {params.value.charAt(0)}
          </Avatar>
          {params.value}
        </Box>
      )
    },
    {
      field: 'subject',
      headerName: 'Subject',
      width: 200
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 100,
      renderCell: (params) => (
        <Chip
          icon={getTypeIcon(params.value)}
          label={params.value.toUpperCase()}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value.toUpperCase()}
          color={getStatusColor(params.value) as any}
          size="small"
        />
      )
    },
    {
      field: 'timestamp',
      headerName: 'Sent At',
      width: 150,
       valueFormatter: (params: any) => new Date(params.value).toLocaleDateString()
    },
    {
      field: 'content',
      headerName: 'Message',
      width: 300,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      )
    }
  ];

  const filteredMessages = messages.filter(message => 
    message.recipientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sentCount = messages.filter(m => m.status === 'sent').length;
  const deliveredCount = messages.filter(m => m.status === 'delivered').length;
  const failedCount = messages.filter(m => m.status === 'failed').length;

  return (
    <Box sx={{ flexGrow: 1 }}>
  {/* Header */}
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
    <Typography variant="h4">
      Communication Center
    </Typography>
    <Button
      variant="contained"
      startIcon={<Add />}
      onClick={handleNewMessage}
    >
      New Message
    </Button>
  </Box>

  {/* Alerts */}
  {failedCount > 0 && (
    <Alert severity="warning" sx={{ mb: 3 }}>
      {failedCount} message(s) failed to send. Please check the message history and retry if needed.
    </Alert>
  )}

  {/* Stats Cards */}
  <Box 
    sx={{ 
      display: 'grid',
      gridTemplateColumns: { 
        xs: '1fr', 
        sm: 'repeat(2, 1fr)', 
        md: 'repeat(4, 1fr)' 
      },
      gap: 3,
      mb: 4 
    }}
  >
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Send color="primary" />
          <Box>
            <Typography color="textSecondary" gutterBottom>
              Total Messages
            </Typography>
            <Typography variant="h4">
              {messages.length}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Check color="success" />
          <Box>
            <Typography color="textSecondary" gutterBottom>
              Delivered
            </Typography>
            <Typography variant="h4">
              {deliveredCount}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Schedule color="info" />
          <Box>
            <Typography color="textSecondary" gutterBottom>
              Sent
            </Typography>
            <Typography variant="h4">
              {sentCount}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Error color="error" />
          <Box>
            <Typography color="textSecondary" gutterBottom>
              Failed
            </Typography>
            <Typography variant="h4">
              {failedCount}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  </Box>

  {/* Message Templates */}
  <Card sx={{ mb: 3 }}>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        Quick Templates
      </Typography>
      <Box 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(4, 1fr)' 
          },
          gap: 2
        }}
      >
        {messageTemplates.map((template) => (
          <Button
            key={template.id}
            variant="outlined"
            fullWidth
            onClick={() => {
              handleTemplateSelect(template.id);
              setOpenDialog(true);
            }}
            sx={{ 
              height: 80, 
              display: 'flex', 
              flexDirection: 'column',
              gap: 1
            }}
          >
            <Typography variant="subtitle2">
              {template.title}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {template.category}
            </Typography>
          </Button>
        ))}
      </Box>
    </CardContent>
  </Card>

  {/* Messages History */}
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Message History
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search messages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          <IconButton>
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      <DataGrid
        rows={filteredMessages}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 }
          }
        }}
        pageSizeOptions={[10, 25, 50]}
        checkboxSelection={false}
        disableRowSelectionOnClick
        autoHeight
        sx={{
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      />
    </CardContent>
  </Card>

  {/* New Message Dialog */}
  <Dialog
    open={openDialog}
    onClose={() => setOpenDialog(false)}
    maxWidth="md"
    fullWidth
  >
    <DialogTitle>
      Send New Message
    </DialogTitle>
    <DialogContent>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
        <FormControl fullWidth required>
          <InputLabel>Message Type</InputLabel>
          <Select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
          >
            <MenuItem value="sms">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Sms /> SMS
              </Box>
            </MenuItem>
            <MenuItem value="whatsapp">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WhatsApp /> WhatsApp
              </Box>
            </MenuItem>
            <MenuItem value="email">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email /> Email
              </Box>
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Template</InputLabel>
          <Select
            value={selectedTemplate}
            onChange={(e) => handleTemplateSelect(e.target.value)}
          >
            <MenuItem value="">Custom Message</MenuItem>
            {messageTemplates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mt: 2 }}>
  <Typography variant="subtitle2" gutterBottom>
    Recipients
  </Typography>
  <Box sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
    <List dense>
      {patients.map((patient) => (
        <ListItem
          key={patient.id}
          onClick={() => {
            const newRecipients = formData.recipients.includes(patient.id)
              ? formData.recipients.filter(id => id !== patient.id)
              : [...formData.recipients, patient.id];
            setFormData({ ...formData, recipients: newRecipients });
          }}
          sx={{ 
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.recipients.includes(patient.id)}
                onChange={() => {}}
              />
            }
            label=""
            sx={{ mr: 1 }}
          />
          <ListItemAvatar>
            <Avatar sx={{ width: 32, height: 32 }}>
              {patient.name.charAt(0)}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={patient.name}
            secondary={`${patient.phone} | ${patient.email}`}
          />
        </ListItem>
      ))}
    </List>
  </Box>
</Box>

      <TextField
        fullWidth
        label="Subject"
        value={formData.subject}
        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
        required
        sx={{ mt: 2 }}
      />

      <TextField
        fullWidth
        label="Message Content"
        multiline
        rows={4}
        value={formData.content}
        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
        required
        placeholder="Type your message here... Use {patient_name} to personalize messages."
        sx={{ mt: 2 }}
      />
    </DialogContent>
    <DialogActions>
      <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
      {formData.recipients.length > 1 ? (
        <Button 
          onClick={handleBulkMessage} 
          variant="contained" 
          startIcon={<Group />}
        >
          Send to {formData.recipients.length} Recipients
        </Button>
      ) : (
        <Button 
          onClick={handleSendMessage} 
          variant="contained" 
          startIcon={<Send />}
        >
          Send Message
        </Button>
      )}
    </DialogActions>
  </Dialog>
</Box>
  );
}